// components/landing/sections/HeroSection.tsx
// ---------------------------------------------------------------------------
// Full-viewport hero: video background, graded overlay, message on the left and
// a single glass card on the right holding the brand mark, the rotating
// sentences and the sign-in surface. Stats strip along the bottom.
//
// The previous layout centred ONE wide card whose left half was a near-opaque
// periwinkle block sitting over the middle of the brand animation, so the
// bubbles survived only around the edges and it read as a login box placed on a
// video. Splitting it lets the animation be the hero and makes the sign-in card
// the only card on screen, which is what gives it prominence.
//
// The overlay is graded (0.82 -> 0.62 -> 0.86): lightest under the headline so
// the animation shows through, deepest behind the card so it stays legible.
// ---------------------------------------------------------------------------

import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import {
  alpha,
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { athenaColors } from "../colors";

import GoogleLoginButton from "../../social_login/GoogleLoginButton";
import LinkedInLoginButton from "../../social_login/LinkedInLoginButton";
import MicrosoftLoginButton from "../../social_login/MicrosoftLoginButton";

import CookieConsent from "components/common/CookieConsent";
import BetaGateCard from "./BetaGateCard";
import RotatingSentences from "./RotatingSentences";

interface Props {
  showLogin: boolean;
  onSignIn: () => void;
  onBackFromLogin: () => void;
  onLearnMore: () => void;
  bypassBetaGate?: boolean;
}

export default function HeroSection({
  showLogin,
  onSignIn,
  onBackFromLogin,
  onLearnMore,
  bypassBetaGate = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [countActive, setCountActive] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setCountActive(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <Box
      id="hero"
      sx={{
        height: isMobile ? "auto" : "100vh",
        minHeight: isMobile ? "100vh" : undefined,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: isMobile ? "visible" : "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: athenaColors.royalBlue,
          zIndex: 0,
        }}
      />

      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      >
        <source src="/images/landing/hero_bg.mp4" type="video/mp4" />
      </video>

      {/* Lighter than v1's overlay on the left, so the animation stays visible
          behind the headline, and deeper on the right where the card sits. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(105deg, ${alpha(
            athenaColors.deepNavy,
            0.82,
          )} 0%, ${alpha(athenaColors.deepNavy, 0.62)} 45%, ${alpha(
            athenaColors.deepNavy,
            0.86,
          )} 100%)`,
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "auto" : "100%",
          minHeight: isMobile ? "100vh" : undefined,
        }}
      >
        <Box
          sx={{
            height: `${(theme.custom?.navbarHeight || 64) + 8}px`,
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 4, md: 6 },
            px: { xs: 3, md: 8, lg: 12 },
            py: { xs: 4, md: 0 },
            maxWidth: 1400,
            width: "100%",
            mx: "auto",
            minHeight: 0,
          }}
        >
          {/* ── LEFT: the message, directly on the animation ── */}
          <Box
            sx={{
              flex: { md: "1 1 55%" },
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              textAlign: { xs: "center", md: "left" },
              maxWidth: 620,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: theme.palette.common.white,
                fontSize: { xs: "2.1rem", md: "3rem", lg: "3.4rem" },
                mb: 2.5,
              }}
            >
              Transform Clinical Data{" "}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${athenaColors.periwinkle} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                into Intelligence
              </Box>
            </Typography>

            <Typography
              sx={{
                color: alpha(theme.palette.common.white, 0.82),
                mb: 4,
                maxWidth: 520,
                lineHeight: 1.7,
                fontSize: { xs: "0.98rem", md: "1.08rem" },
              }}
            >
              The AI-powered oncology platform that extracts, structures, and
              analyzes patient data across 44 cancer types. From molecular
              genetics to treatment outcomes, fully automated.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Button
                variant="contained"
                onClick={onSignIn}
                sx={{
                  px: 3.5,
                  py: 1.1,
                  fontSize: "1rem",
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: "none",
                  // `background`, NOT `backgroundColor`: the theme sets a
                  // royalBlue gradient IMAGE on .MuiButton-contained, which
                  // paints over any background-color and would hide this fill.
                  //
                  // Orange is this design system's ACTION colour and the same
                  // one the navbar CTA uses, which fires the SAME onSignIn
                  // handler, so the two primary actions now read as one thing.
                  // Periwinkle was tried first and blends into the lavender
                  // bubbles of the backdrop.
                  //
                  // Ink is deepNavy, NOT white: white on orange is 2.59:1 and
                  // 1.78:1 at the light end of the gradient, which is what the
                  // navbar CTA currently ships. deepNavy gives 6.18:1 rising to
                  // 9.01:1, so the brand colour is kept and the contrast fixed.
                  background: `linear-gradient(135deg, ${athenaColors.orange} 0%, ${athenaColors.lightOrange} 100%)`,
                  color: athenaColors.deepNavy,
                  border: "none",
                  "&:hover": {
                    // Reversed gradient, the navbar's hover idiom.
                    background: `linear-gradient(135deg, ${athenaColors.lightOrange} 0%, ${athenaColors.orange} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 16px ${alpha(athenaColors.orange, 0.3)}`,
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Log In
              </Button>
              <Button
                variant="outlined"
                onClick={onLearnMore}
                sx={{
                  px: 3.5,
                  py: 1.1,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: "none",
                  borderColor: alpha(theme.palette.common.white, 0.5),
                  color: theme.palette.common.white,
                  "&:hover": {
                    borderColor: theme.palette.common.white,
                    background: alpha(theme.palette.common.white, 0.14),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Learn More
              </Button>
            </Box>
          </Box>

          {/* ── RIGHT: the one card. Brand, then the sign-in surface. ── */}
          <Box
            sx={{
              flex: { md: "1 1 45%" },
              width: "100%",
              maxWidth: 460,
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              minHeight: { md: 420 },
              background: alpha(athenaColors.deepNavy, 0.55),
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: `1px solid ${alpha(theme.palette.common.white, 0.22)}`,
              boxShadow: `0 24px 80px ${alpha(athenaColors.deepNavy, 0.5)}`,
              overflowY: showLogin ? "auto" : "hidden",
            }}
          >
            {!showLogin ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    component="img"
                    src="/brand/inline/athena_rwe_white_path.svg"
                    alt="Athena RWE"
                    sx={{ height: 44, width: "auto" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      letterSpacing: "0.2em",
                      fontWeight: 700,
                      color: alpha(athenaColors.periwinkle, 0.85),
                      textTransform: "uppercase",
                    }}
                  >
                    Clinical Intelligence Platform
                  </Typography>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <RotatingSentences variant="dark" />
                </Box>
              </>
            ) : (
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                {bypassBetaGate ? (
                  <>
                    <Typography
                      variant="h5"
                      align="center"
                      sx={{ color: theme.palette.common.white, fontWeight: 700, mb: 2.5 }}
                    >
                      Sign In
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        maxWidth: 320,
                        mb: 2,
                      }}
                    >
                      <GoogleLoginButton />
                      <MicrosoftLoginButton />
                      <LinkedInLoginButton />
                    </Box>
                    <Box
                      sx={{
                        display: { xs: "none", md: "flex" },
                        alignItems: "flex-start",
                        gap: 0.75,
                        width: "100%",
                        maxWidth: 320,
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        background: alpha(theme.palette.common.white, 0.08),
                      }}
                    >
                      <VerifiedUserOutlinedIcon
                        sx={{ fontSize: 16, color: athenaColors.periwinkle, mt: "1px" }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: alpha(theme.palette.common.white, 0.75), lineHeight: 1.45 }}
                      >
                        Enterprise SSO and institutional accounts are configured
                        on request.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <BetaGateCard />
                )}

                <Button
                  onClick={onBackFromLogin}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    color: alpha(theme.palette.common.white, 0.75),
                    "&:hover": { color: theme.palette.common.white },
                  }}
                >
                  Back
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CookieConsent />
        </Box>

        {/* Stats strip, shared with v1 so the numbers cannot diverge. */}
        <Box
          sx={{
            pb: { xs: 4, md: 5 },
            pt: { xs: 1.5, md: 3 },
            px: 2,
            flexShrink: 0,
            background: `linear-gradient(0deg, ${alpha(
              athenaColors.deepNavy,
              0.5,
            )} 0%, transparent 100%)`,
          }}
        >
          <HeroStatsStrip trigger={countActive} />
        </Box>
      </Box>
    </Box>
  );
}

const STATS = [
  // 44 exactly: the cancer.* registry minus the `other` catch-all.
  { value: 44, suffix: "", label: "Cancer Types" },
  { value: 23, suffix: "k+", label: "Patient Records" },
  { value: 35, suffix: "+", label: "Hospitals & Institutions" },
  { value: 4, suffix: "", label: "Countries" },
];

function useCountUp(target: number, duration: number, trigger: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, trigger]);
  return count;
}

// ---------------------------------------------------------------------------
// Stats strip, shared by both hero variants so the numbers cannot diverge.
// ---------------------------------------------------------------------------
function HeroStatsStrip({ trigger }: { trigger: boolean }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: { xs: 3, md: 8 },
        flexWrap: "wrap",
        maxWidth: 900,
        mx: "auto",
      }}
    >
      {STATS.map((stat) => (
        <StatItem key={stat.label} stat={stat} trigger={trigger} />
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Stat counter item
// ---------------------------------------------------------------------------
function StatItem({
  stat,
  trigger,
}: {
  stat: (typeof STATS)[0];
  trigger: boolean;
}) {
  const theme = useTheme();
  const count = useCountUp(stat.value, 1500, trigger);
  return (
    <Box sx={{ textAlign: "center", minWidth: { xs: 70, md: 90 } }}>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          //fontSize: { xs: "2rem", md: "2.5rem" },
          fontSize: { xs: "1.8rem", md: "2rem", lg: "2.5rem" },
          color: theme.palette.common.white,
          lineHeight: 1,
        }}
      >
        {count}
        {stat.suffix}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: alpha(theme.palette.common.white, 0.7),
          fontWeight: 500,
          mt: 0.5,
          fontSize: { xs: "0.7rem", md: "0.75rem", lg: "0.85rem" },
        }}
      >
        {stat.label}
      </Typography>
    </Box>
  );
}
